import { useState, useCallback } from "react";
import { API } from "../App";
import { Code2, Play, RotateCcw, ChevronDown, ChevronUp, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const TEMPLATE_CONFIGS = {
  cpk_calculator: {
    title: "Cpk Calculator",
    description: "Compute process capability index from measurement data",
    defaults: { data: "2.50, 2.52, 2.49, 2.51, 2.48, 2.53, 2.50, 2.51", USL: "2.60", LSL: "2.40" },
    fields: [
      { key: "data", label: "Measurements (comma-separated)", type: "textarea", placeholder: "2.50, 2.52, 2.49, 2.51..." },
      { key: "USL", label: "Upper Spec Limit (USL)", type: "number", step: "0.01" },
      { key: "LSL", label: "Lower Spec Limit (LSL)", type: "number", step: "0.01" },
    ],
    parseInputs: (vals) => ({
      data: vals.data.split(",").map((v) => parseFloat(v.trim())).filter((v) => !isNaN(v)),
      USL: parseFloat(vals.USL),
      LSL: parseFloat(vals.LSL),
    }),
    renderResults: (r) => {
      const pass = r.class_iii_pass;
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {["CPU", "CPL", "Cpk"].map((k) => (
              <div key={k} className="bg-[#132340] rounded-[3px] p-3 text-center">
                <div className="text-[10px] text-slate-500 uppercase">{k}</div>
                <div className={`text-lg font-bold font-mono ${k === "Cpk" ? (pass ? "text-emerald-400" : "text-red-400") : "text-white"}`}>
                  {r.results[k]?.toFixed(3)}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="flex items-center gap-1">
              <span className="text-slate-500">n =</span>
              <span className="text-white font-mono">{r.results.n}</span>
            </div>
            <span className="text-slate-600">|</span>
            <div className="flex items-center gap-1">
              <span className="text-slate-500">mean =</span>
              <span className="text-white font-mono">{r.results.mean?.toFixed(4)}</span>
            </div>
            <span className="text-slate-600">|</span>
            <div className="flex items-center gap-1">
              <span className="text-slate-500">sigma =</span>
              <span className="text-white font-mono">{r.results.sigma?.toFixed(4)}</span>
            </div>
          </div>
          <div className={`flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-[3px] ${pass ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
            {pass ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            {r.assessment}
            <span className="text-[10px] font-normal ml-auto">{pass ? "Class III: PASS" : "Class III: FAIL"}</span>
          </div>
        </div>
      );
    },
  },
  aql_plan: {
    title: "AQL Sampling Plan",
    description: "Determine sample size and accept/reject numbers per ISO 2859-1",
    defaults: { lot_size: "750", aql: "0.065" },
    fields: [
      { key: "lot_size", label: "Lot Size", type: "number", step: "1" },
      { key: "aql", label: "AQL Level", type: "select", options: [
        { value: "0.065", label: "0.065 (Tightest)" },
        { value: "0.10", label: "0.10" },
        { value: "0.25", label: "0.25" },
      ]},
    ],
    parseInputs: (vals) => ({ lot_size: parseInt(vals.lot_size), aql: parseFloat(vals.aql) }),
    renderResults: (r) => (
      <div className="space-y-3">
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: "Code", val: r.results.code, color: "text-sky-400" },
            { label: "Sample (n)", val: r.results.n, color: "text-white" },
            { label: "Accept (Ac)", val: r.results.Ac, color: "text-emerald-400" },
            { label: "Reject (Re)", val: r.results.Re, color: "text-red-400" },
          ].map((item) => (
            <div key={item.label} className="bg-[#132340] rounded-[3px] p-3 text-center">
              <div className="text-[10px] text-slate-500 uppercase">{item.label}</div>
              <div className={`text-lg font-bold font-mono ${item.color}`}>{item.val}</div>
            </div>
          ))}
        </div>
        {r.note && (
          <div className="flex items-start gap-2 text-xs bg-amber-500/10 text-amber-400 px-3 py-2 rounded-[3px]">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{r.note}</span>
          </div>
        )}
      </div>
    ),
  },
  weibull_fit: {
    title: "Weibull Fit",
    description: "Fit 2-parameter Weibull to failure time data",
    defaults: { data: "100, 200, 350, 400, 800, 150, 600, 250" },
    fields: [
      { key: "data", label: "Failure Times (comma-separated)", type: "textarea", placeholder: "100, 200, 350, 400..." },
    ],
    parseInputs: (vals) => ({
      data: vals.data.split(",").map((v) => parseFloat(v.trim())).filter((v) => !isNaN(v) && v > 0),
    }),
    renderResults: (r) => {
      const beta = r.results.beta;
      const mode = r.interpretation;
      const modeColor = beta < 1 ? "text-red-400" : beta < 1.5 ? "text-amber-400" : "text-emerald-400";
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#132340] rounded-[3px] p-3 text-center">
              <div className="text-[10px] text-slate-500 uppercase">Beta (Shape)</div>
              <div className={`text-lg font-bold font-mono ${modeColor}`}>{beta?.toFixed(3)}</div>
            </div>
            <div className="bg-[#132340] rounded-[3px] p-3 text-center">
              <div className="text-[10px] text-slate-500 uppercase">Eta (Scale)</div>
              <div className="text-lg font-bold font-mono text-white">{r.results.eta?.toFixed(1)}</div>
            </div>
          </div>
          <div className={`flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-[3px] bg-[#132340] ${modeColor}`}>
            {mode}
          </div>
          <div className="text-[10px] text-slate-500">
            Method: {r.results.method?.replace(/_/g, " ")} | n = {r.results.n}
          </div>
        </div>
      );
    },
  },
  aging_projection: {
    title: "Aging Projection (Arrhenius)",
    description: "Calculate accelerated aging factor per ASTM F1980",
    defaults: { Ea: "0.8", T_test_C: "55", T_use_C: "25" },
    fields: [
      { key: "Ea", label: "Activation Energy (eV)", type: "number", step: "0.05" },
      { key: "T_test_C", label: "Test Temperature (C)", type: "number", step: "1" },
      { key: "T_use_C", label: "Use/Storage Temperature (C)", type: "number", step: "1" },
    ],
    parseInputs: (vals) => ({ Ea: parseFloat(vals.Ea), T_test_C: parseFloat(vals.T_test_C), T_use_C: parseFloat(vals.T_use_C) }),
    renderResults: (r) => (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-[#132340] rounded-[3px] p-3 text-center">
            <div className="text-[10px] text-slate-500 uppercase">Arrhenius AAF</div>
            <div className="text-lg font-bold font-mono text-cyan-400">{r.results.AAF_arrhenius?.toFixed(1)}x</div>
          </div>
          <div className="bg-[#132340] rounded-[3px] p-3 text-center">
            <div className="text-[10px] text-slate-500 uppercase">Q10=2 AAF</div>
            <div className="text-lg font-bold font-mono text-white">{r.results.AAF_Q10?.toFixed(1)}x</div>
          </div>
        </div>
        <div className="bg-[#132340] rounded-[3px] p-3 text-xs text-slate-300">
          {r.interpretation}
        </div>
      </div>
    ),
  },
};

const InlineCalculator = ({ templateId, contextNote }) => {
  const config = TEMPLATE_CONFIGS[templateId];
  const [expanded, setExpanded] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [inputs, setInputs] = useState(config?.defaults || {});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [code, setCode] = useState(null);

  const fetchCode = useCallback(async () => {
    if (code) return;
    try {
      const res = await fetch(`${API}/templates/python/${templateId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCode(data.code);
      }
    } catch {}
  }, [templateId, code]);

  const compute = async () => {
    if (!config) return;
    setLoading(true);
    setError(null);
    try {
      const parsed = config.parseInputs(inputs);
      const res = await fetch(`${API}/templates/python/${templateId}/compute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsed),
        credentials: "include",
      });
      if (res.ok) {
        setResult(await res.json());
      } else {
        const err = await res.json();
        setError(err.detail || "Computation failed");
      }
    } catch (e) {
      setError("Failed to compute");
    }
    setLoading(false);
  };

  const reset = () => {
    setInputs(config?.defaults || {});
    setResult(null);
    setError(null);
  };

  if (!config) return null;

  return (
    <div className="bg-[#0A1628] border border-[#1E3A5F]/50 rounded-[3px] overflow-hidden" data-testid={`inline-calc-${templateId}`}>
      {/* Header — always visible */}
      <button
        onClick={() => { setExpanded(!expanded); if (!expanded) fetchCode(); }}
        className="w-full flex items-center justify-between p-4 hover:bg-[#132340]/50 transition-colors text-left"
        data-testid={`inline-calc-toggle-${templateId}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-[3px] bg-lime-500/10 flex items-center justify-center shrink-0">
            <Code2 className="w-4 h-4 text-lime-400" />
          </div>
          <div>
            <div className="text-lime-400 text-xs font-bold">{config.title}</div>
            <div className="text-slate-500 text-[10px]">{config.description}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!expanded && (
            <span className="text-[9px] bg-lime-500/10 text-lime-400 px-2 py-0.5 rounded font-bold uppercase">Try It</span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
      </button>

      {/* Expandable panel */}
      {expanded && (
        <div className="border-t border-[#1E3A5F]/40 p-4 space-y-4">
          {/* Context note from FME */}
          {contextNote && (
            <div className="text-[10px] text-slate-500 italic border-l-2 border-lime-500/30 pl-2">{contextNote}</div>
          )}

          {/* Input fields */}
          <div className="space-y-2">
            <div className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Inputs</div>
            {config.fields.map((f) => (
              <div key={f.key}>
                <label className="text-[10px] text-slate-500 block mb-1">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea
                    value={inputs[f.key] || ""}
                    onChange={(e) => setInputs({ ...inputs, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    rows={2}
                    className="w-full bg-[#132340] border border-[#1E3A5F] rounded-[3px] px-3 py-2 text-xs text-white font-mono focus:border-lime-500/50 focus:outline-none resize-none"
                    data-testid={`calc-input-${f.key}`}
                  />
                ) : f.type === "select" ? (
                  <select
                    value={inputs[f.key] || ""}
                    onChange={(e) => setInputs({ ...inputs, [f.key]: e.target.value })}
                    className="w-full bg-[#132340] border border-[#1E3A5F] rounded-[3px] px-3 py-2 text-xs text-white focus:border-lime-500/50 focus:outline-none"
                    data-testid={`calc-input-${f.key}`}
                  >
                    {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input
                    type="number"
                    value={inputs[f.key] || ""}
                    onChange={(e) => setInputs({ ...inputs, [f.key]: e.target.value })}
                    step={f.step}
                    className="w-full bg-[#132340] border border-[#1E3A5F] rounded-[3px] px-3 py-2 text-xs text-white font-mono focus:border-lime-500/50 focus:outline-none"
                    data-testid={`calc-input-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={compute}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-2 bg-lime-600 hover:bg-lime-500 text-white text-xs font-bold rounded-[3px] transition-colors disabled:opacity-50"
              data-testid={`calc-run-${templateId}`}
            >
              <Play className="w-3 h-3" /> {loading ? "Computing..." : "Compute"}
            </button>
            <button
              onClick={reset}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#132340] hover:bg-[#1E3A5F] text-slate-400 text-xs rounded-[3px] transition-colors"
              data-testid={`calc-reset-${templateId}`}
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
            <button
              onClick={() => { setShowCode(!showCode); fetchCode(); }}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded-[3px] transition-colors ml-auto ${showCode ? "bg-lime-500/10 text-lime-400" : "bg-[#132340] text-slate-500 hover:text-slate-300"}`}
              data-testid={`calc-code-toggle-${templateId}`}
            >
              <Code2 className="w-3 h-3" /> {showCode ? "Hide Code" : "View Code"}
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-[3px] p-3 text-red-400 text-xs" data-testid="calc-error">
              {error}
            </div>
          )}

          {/* Results */}
          {result && (
            <div data-testid={`calc-results-${templateId}`}>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-2">Results</div>
              {config.renderResults(result)}
            </div>
          )}

          {/* Code viewer */}
          {showCode && code && (
            <div data-testid={`calc-code-${templateId}`}>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-2">Python Source</div>
              <pre className="bg-[#0D1117] border border-[#1E3A5F]/50 rounded-[3px] p-4 overflow-x-auto text-[11px] leading-relaxed text-slate-300 font-mono max-h-64 overflow-y-auto">
                {code}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InlineCalculator;
